/**
 * Configuração robusta de ambiente com validação
 * Centraliza e valida todas as variáveis de ambiente
 */

import { normalizeTimestamp } from '../utils/timestampNormalizer';

// Interface para tipagem das variáveis de ambiente
interface EnvironmentConfig {
  supabase: {
    url: string;
    anonKey: string;
  };
  app: {
    name: string;
    version: string;
    environment: 'development' | 'production' | 'test';
  };
  features: {
    enableDebugMode: boolean;
    enableOfflineMode: boolean;
    enableRealTimeSync: boolean;
  };
  tracking: {
    gtmId: string;
    ga4MeasurementId: string;
    facebookPixelId: string;
    clarityProjectId: string;
    demoVideoUrl: string;
  };
}

// Validadores para cada tipo de variável
const validators = {
  supabaseUrl: (url: string): boolean => {
    const pattern = /^https:\/\/[a-zA-Z0-9-]+\.supabase\.co$/;
    return pattern.test(url);
  },
  
  supabaseKey: (key: string): boolean => {
    // Validação mais flexível para chaves JWT do Supabase
    const pattern = /^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*$/;
    return pattern.test(key) && key.length > 100; // JWT válido deve ter mais de 100 caracteres
  },
  
  environment: (env: string): env is 'development' | 'production' | 'test' => {
    return ['development', 'production', 'test'].includes(env);
  }
};

// Função para obter e validar variável de ambiente
function getEnvVar(key: string, defaultValue?: string, validator?: (value: string) => boolean): string {
  const value = process.env[key] || defaultValue;
  
  if (!value) {
    throw new Error(`Variável de ambiente obrigatória não encontrada: ${key}`);
  }
  
  if (validator && !validator(value)) {
    throw new Error(`Formato inválido para variável de ambiente: ${key}`);
  }
  
  return value;
}

// Função para obter variável booleana
function getBooleanEnvVar(key: string, defaultValue: boolean = false): boolean {
  const value = process.env[key];
  if (!value) return defaultValue;
  
  return ['true', '1', 'yes', 'on'].includes(value.toLowerCase());
}

// Configuração principal
function createEnvironmentConfig(): EnvironmentConfig {
  try {
    const config: EnvironmentConfig = {
      supabase: {
        url: getEnvVar('REACT_APP_SUPABASE_URL', undefined, validators.supabaseUrl),
        anonKey: getEnvVar('REACT_APP_SUPABASE_ANON_KEY', undefined) // Removendo validação temporariamente
      },
      app: {
        name: getEnvVar('REACT_APP_NAME', 'PsicolSys'),
        version: getEnvVar('REACT_APP_VERSION', '1.0.0'),
        environment: process.env.NODE_ENV === 'production' ? 'production' : 
                    process.env.NODE_ENV === 'test' ? 'test' : 'development'
      },
      features: {
        enableDebugMode: getBooleanEnvVar('REACT_APP_DEBUG_MODE', process.env.NODE_ENV === 'development'),
        enableOfflineMode: getBooleanEnvVar('REACT_APP_OFFLINE_MODE', true),
        enableRealTimeSync: getBooleanEnvVar('REACT_APP_REALTIME_SYNC', true)
      },
      tracking: {
        // Defaults intencionais: produção configura via Vercel env vars.
        // Hardcoded fallback fazia staging/preview/local com env var vazia
        // disparar pixel de PRODUÇÃO — contaminando Meta Ad account com tráfego
        // de teste e Lookalike Audiences. Empty string = não injeta.
        gtmId: process.env.REACT_APP_GTM_ID || '',
        ga4MeasurementId: process.env.REACT_APP_GA4_ID || '',
        facebookPixelId: process.env.REACT_APP_FB_PIXEL_ID || '',
        clarityProjectId: process.env.REACT_APP_CLARITY_PROJECT_ID || '',
        demoVideoUrl: process.env.REACT_APP_DEMO_VIDEO_URL || ''
      }
    };
    
    // Validações adicionais para produção
    if (config.app.environment === 'production') {
      if (config.supabase.url.includes('localhost')) {
        throw new Error('URL do Supabase não pode ser localhost em produção');
      }
      
      if (config.features.enableDebugMode) {
        console.warn('⚠️  Modo debug está habilitado em produção');
      }
    }
    
    return config;
  } catch (error) {
    console.error('❌ Erro na configuração de ambiente:', error);
    throw error;
  }
}

// Exportar configuração validada
export const env = createEnvironmentConfig();

// Função para verificar se o ambiente está configurado corretamente
export function validateEnvironment(): boolean {
  try {
    createEnvironmentConfig();
    return true;
  } catch (error) {
    console.error('Validação de ambiente falhou:', error);
    return false;
  }
}

// Função para obter informações de debug (apenas em desenvolvimento)
export function getEnvironmentInfo(): Record<string, any> {
  if (env.app.environment === 'production') {
    return { environment: 'production' };
  }
  
  return {
    environment: env.app.environment,
    features: env.features,
    supabaseConfigured: !!(env.supabase.url && env.supabase.anonKey),
    timestamp: normalizeTimestamp(new Date())
  };
}

// Log de inicialização (apenas em desenvolvimento)
if (env.features.enableDebugMode) {
  console.log('🔧 Configuração de ambiente carregada:', getEnvironmentInfo());
}